/*
 * kp-copy-code.js — the <kp-copy-code> element: tapping a discount code
 * attaches it to the cart through /cart/update.js — the one cart endpoint
 * kp-owned code may call, in the shape ADR 0001 sets out — and copies it to
 * the clipboard as a fallback. It used to live in kp-pdp.js; it moved out
 * when the product card grew a coupon strip, so a row of cards on the
 * homepage can load this and nothing else of the PDP.
 *
 * Wrapped in an IIFE: every kp-*.js is a classic <script src>, and a page
 * can carry this file twice (layout for the cards, PDP for the panel). A
 * bare lexical declaration would then throw `already declared` and kill the
 * whole file; inside a function scope it is harmless, and customElements.get()
 * keeps the define idempotent. (Ticket #22.)
 *
 * It also draws the cart back itself — the drawer, the cart page's lines and
 * footer, the header bubble — from the sections the same request returns,
 * exactly as kp-cart-discount does. Left to Dawn, /cart refreshed only its
 * lines, and the money panel, the ladder box and the checkout label went on
 * quoting the old total until a reload (found on the preview, 2026-09-24).
 *
 * The price ladder (docs/prototypes/called-shot-ladder/BUILD.md) puts one
 * code in several places on a page — the card's chip, the product page's
 * code button, the cart's box — so a code on the cart is the page's state,
 * not one element's. What the ladder builds on:
 *
 *  - `kp:code-applied` on document, detail.code (upcased): after every tap
 *    or kpApplyCode that leaves the code applied, and when any other cart
 *    answer newly lists one (the drawer's code field). Codes that were on
 *    the cart before the page's first cart answer are learned from it, not
 *    announced. The event marks a moment; whether a code is applied NOW is
 *    the data-applied attribute below.
 *  - Every <kp-copy-code> whose data-code is that code shows it applied —
 *    its button's data-state="applied" — and carries a `data-applied`
 *    attribute, so CSS can restyle the rest of a ladder with :has(). Both go
 *    again when a cart answer stops listing the code (removed in the
 *    drawer). Liquid may render an element with data-applied already, for a
 *    cart that has the code when the page is built.
 *  - window.kpApplyCode(code, { signal } = {}) → Promise<boolean> attaches a
 *    code with no element, in the same shape: the product page's buy button
 *    and sticky bar, the popup. It resolves true when the cart takes the
 *    code (an empty cart counts: the code applies the moment a cue lands in
 *    it), false when the cart refuses it for what is in it now or cannot be
 *    reached, and never rejects. Either way the code stays on the cart, so a
 *    caller about to add a cue adds it whatever the answer. Posting a code
 *    the cart already has changes nothing, so no caller needs to check first.
 *    `signal`, an AbortSignal, calls it off: both of its requests carry it,
 *    and once it fires the promise resolves false having written no section,
 *    published no cartUpdate and fired no kp:code-applied, so a late answer
 *    never paints over a newer drawer. The code may still have reached the
 *    cart; the next render shows it. An element's own tap takes the same
 *    guard when the element leaves the page while it waits.
 *  - With or without a signal, an answer (a tap's or kpApplyCode's) that
 *    lands after the cart changed some other way — Dawn's add, a quantity
 *    step, another apply painted first — is older than the cart on screen:
 *    it paints nothing, publishes nothing and leaves data-applied alone,
 *    and its verdict is still what the promise resolves with (`generation`).
 *  - `data-form` on an element in apply mode (the product page's code
 *    button, kp-pdp-ladder.liquid) is the id of Dawn's product form, and
 *    the tap goes on to the cart (owner, 2026-09-25). Once the cart has
 *    answered, a cue it does not hold yet is added through that form —
 *    Dawn's own add, which opens the drawer as its button does — and a cue
 *    it holds already only gets the drawer opened, so a second tap never
 *    adds a second cue. A refused or unreachable code does not stop it (the
 *    drawer's box offers the code again), and nothing is copied: the drawer
 *    shows the code on the cart.
 *
 * None of this keeps cart state. What the page shows is the platform's own
 * verdict (discount_codes) from the last cart answer that carried one, and
 * the one thing held between answers is that verdict, so an element Dawn
 * re-renders (the drawer) comes back applied without asking the cart again.
 */
(() => {
// Once per page, and the registry is the flag. Dawn's product swap re-runs
// the scripts inside <main> (CONTEXT invariant 6): the cart subscription and
// window.kpApplyCode below must each exist once, not once per run.
if (customElements.get('kp-copy-code')) return;

const APPLIED_EVENT = 'kp:code-applied';

/*
 * Every cart surface a code moves, and the part of each that is replaced:
 * kp-cart-discount.js's own list and pairs (its TARGETS, Dawn's from
 * getSectionsToRender in cart.js and cart-drawer.js) — keep the two in step.
 * The drawer, the cart page's lines and footer (the money panel, the ladder
 * box, the checkout label), the header bubble. A code changes the one cart
 * all four are drawn from, so all four that are on the page are rendered
 * back, whoever asked: an element's `data-sections` is no longer read, since
 * the only thing a caller could do with it was leave one of them stale.
 */
const TARGETS = {
  'cart-drawer': { target: '.drawer__inner', selector: '.drawer__inner' },
  'main-cart-items': { target: '#main-cart-items', selector: '.js-contents' },
  'main-cart-footer': { target: '#main-cart-footer', selector: '.js-contents' },
  'cart-icon-bubble': { target: '#cart-icon-bubble', selector: '.shopify-section' },
};

// The codes the last cart answer counted as applied, and whether the page
// has had an answer yet: before the first there is nothing to compare with.
let appliedCodes = new Set();
let answered = false;

/*
 * How many times the cart on screen has changed: every cartUpdate this file
 * did not publish (Dawn's add and quantity steppers, the drawer's code
 * field, a refresh through Shopify.actions), and every answer this file
 * paints itself. An apply notes the count when it starts. Two applies can
 * be in flight at once — the product page's code chip, then its buy button,
 * whose own apply is followed by Dawn's add — and the slower answer is the
 * older cart: painted last, it put the pre-add drawer back over the newer
 * one. So an answer that lands after the count moved paints nothing,
 * publishes nothing and leaves data-applied alone. The newer cart is on
 * screen already, drawn by the flow that changed it; the caller still gets
 * the answer's verdict.
 */
let generation = 0;
// Marks this file's own publishes, so its listener does not count them twice.
const OWN = Symbol('kp-copy-code');
// Buttons already listening: an element moved in the DOM connects twice,
// and must not attach twice per tap.
const bound = new WeakSet();

const normalise = (code) => String(code || '').trim().toUpperCase();

/*
 * Which codes a cart answer counts as applied: the verdict a tap has always
 * given. A code the platform lists and does not refuse — and `applicable:
 * false` on an EMPTY cart is not a refusal: with nothing to allocate against
 * the platform says no to every code, real or not. It is only reported as
 * refused when there are items for it to have applied to.
 */
function appliedIn(state) {
  return (state.discount_codes || [])
    .filter((entry) => entry.code && !(entry.applicable === false && state.item_count > 0))
    .map((entry) => normalise(entry.code));
}

/*
 * Show a cart answer on the page: every element to its verdict, then the
 * event for the code this answer was asked to apply (`applying`) and for
 * any other that has just become applied. The page's first answer only
 * teaches it what was on the cart already. An answer without the code list
 * says nothing about codes and changes nothing — Dawn's add to cart answers
 * with the added line, not the cart.
 */
function reflect(state, applying) {
  if (!state || !Array.isArray(state.discount_codes)) return;
  const before = answered ? appliedCodes : null;
  answered = true;
  appliedCodes = new Set(appliedIn(state));
  document.querySelectorAll('kp-copy-code').forEach((element) => element.sync?.());
  appliedCodes.forEach((code) => {
    if (code === applying || (before && !before.has(code))) {
      document.dispatchEvent(new CustomEvent(APPLIED_EVENT, { detail: { code } }));
    }
  });
}

// A cart that cannot be read reads as no codes; one whose read was called
// off stays called off.
function currentCodes(signal) {
  return fetch(`${routes.cart_url}.js`, { signal })
    .then((response) => response.json())
    .then((cart) => (Array.isArray(cart.discount_codes) ? cart.discount_codes.map((entry) => entry.code) : []))
    .catch((error) => {
      if (signal?.aborted) throw error;
      return [];
    });
}

const aborted = () => new DOMException('The apply was called off', 'AbortError');

/*
 * The sections to ask for: only those whose target is on this page — the
 * drawer is a merchant setting, and a section asked for with nothing to
 * receive it is a render the shopper waits on for nothing. A section in a
 * JSON template has a template-scoped id, which Liquid writes into data-id
 * (Dawn reads the same attribute); asked for by its file name it comes back
 * standalone, without its blocks, which on the cart page are the money panel
 * and the checkout button. The drawer and the bubble have no data-id, and
 * their file name is their id.
 */
function sectionsOnPage() {
  return Object.entries(TARGETS)
    .map(([name, pair]) => {
      const root = document.querySelector(pair.target);
      return root ? { name, section: root.dataset.id || name } : null;
    })
    .filter(Boolean);
}

/*
 * Write each returned section into its place: kp-cart-discount's
 * renderSections. The answer is the whole section, wrapper included, so only
 * the region inside it moves; a section with no inner region is replaced
 * whole, Dawn's own fallback.
 */
function render(state, wanted) {
  wanted.forEach(({ name, section }) => {
    const html = state.sections?.[section];
    const pair = TARGETS[name];
    if (!html || !pair) return;

    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(pair.selector);
    const root = document.querySelector(pair.target);
    const target = root?.querySelector(pair.selector) || root;

    if (source && target) target.innerHTML = source.innerHTML;
  });
}

/*
 * The cart call is the ADR 0001 shape and nothing more: Dawn's fetchConfig,
 * the platform's own `discount` parameter, the cart's sections rendered back
 * in the same request and written into the page, then cartUpdate published
 * so Dawn's listeners follow. The platform only ever sets the whole code
 * list, so the codes already on the cart go back with this one. Nothing here
 * computes cart state: the page shows what the response says.
 *
 * Resolves with the cart's verdict on the code and the cart it answered
 * with ({ applied, cart }); rejects when the cart cannot be reached. The
 * cart is only read by the caller that asked, at once, never kept. The
 * verdict is shown before cartUpdate goes out, so this
 * file's own listener finds nothing new in it and the event fires once.
 * `cart-items` is Dawn's flag for "the sections are already rendered, do not
 * fetch them again", the one its guard in cart.js reads (kp-cart-discount
 * sends the same).
 *
 * `signal` (an AbortSignal, optional) calls the apply off. Both requests
 * carry it, and once it has fired nothing here touches the page: no section
 * is written, no cartUpdate goes out, no event fires, and the promise
 * rejects with an AbortError. An answer that arrives after its caller moved
 * on (the buy button's add went ahead) must never paint over the newer cart.
 * The last check is the moment before the first write; from there the
 * answer is on the page, whole.
 */
async function attachCode(code, signal) {
  const started = generation;
  const known = await currentCodes(signal);
  const codes = known.some((entry) => normalise(entry) === code) ? known : [...known, code];
  const wanted = sectionsOnPage();
  const body = JSON.stringify({
    discount: codes.join(','),
    sections: wanted.map((entry) => entry.section),
    sections_url: window.location.pathname,
  });

  const state = await fetch(routes.cart_update_url, { ...fetchConfig(), body, signal }).then((response) =>
    response.json()
  );
  if (signal?.aborted) throw aborted();
  if (!state || state.errors) throw new Error(state?.errors || 'Cart update failed');

  const answer = { applied: appliedIn(state).includes(code), cart: state };
  // The cart changed while this answer was on its way: it is older than
  // what is on screen (see `generation`). The verdict, not the render.
  if (generation !== started) return answer;

  render(state, wanted);
  generation += 1;
  reflect(state, code);
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state, [OWN]: true });
  return answer;
}

class KpCopyCode extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-kp-copy]');
    this.toast = this.querySelector('[data-kp-toast]');
    if (!this.button) return;
    if (!bound.has(this.button)) {
      bound.add(this.button);
      this.button.addEventListener('click', this.activate.bind(this));
    }
    // Rendered applied by Liquid (the cart had the code when the page was
    // built): the cart's word too, until a cart answer says otherwise.
    if (this.code && (this.hasAttribute('data-applied') || this.button.dataset.state === 'applied')) {
      appliedCodes.add(this.code);
    }
    this.sync();
  }

  /*
   * Taken off the page while its tap is still waiting on the cart: the
   * region it sat in was replaced by a newer render (a quantity change in
   * the drawer, a product swap), so its answer is late and is called off
   * rather than painted over the newer cart. A move in the DOM reconnects
   * before the microtask runs and is not that.
   */
  disconnectedCallback() {
    const pending = this.pending;
    if (!pending) return;
    queueMicrotask(() => {
      if (!this.isConnected) pending.abort();
    });
  }

  get code() {
    return normalise(this.dataset.code);
  }

  /* This element, to the verdict of the last cart answer. */
  sync() {
    if (!this.button) return;
    const applied = appliedCodes.has(this.code);
    this.toggleAttribute('data-applied', applied);
    // A tap still waiting on the cart keeps its bars; its own answer lands next.
    if (this.button.dataset.state === 'busy') return;
    this.setState(applied ? 'applied' : 'idle');
    if (!applied && this.toast?.classList.contains('kp-offer__toast--stay')) {
      clearTimeout(this.timer);
      this.toast.hidden = true;
      this.toast.classList.remove('kp-offer__toast--stay');
    }
  }

  /*
   * Attach first, copy second. The cart is what makes the code worth money,
   * and the clipboard is the fallback for a customer who checks out later.
   * The attach is attachCode above, which re-renders the cart and shows its
   * verdict on every element carrying the code, this one included — unless
   * this one was inside the cart it re-rendered (see announce). An element
   * with data-form goes on to the cart instead of copying (toCart).
   */
  async activate() {
    const code = this.code;
    if (!code || this.button.dataset.state === 'busy') return;

    if (this.dataset.mode === 'copy') {
      await this.copy(code);
      return this.announce(this.dataset.msgCopied);
    }

    // The wait is shown at once — the bars in the button and aria-busy — so
    // the tap has an answer before the cart does.
    this.setState('busy');
    const pending = new AbortController();
    this.pending = pending;
    let applied = false;
    let cart = null;
    let message;
    try {
      ({ applied, cart } = await attachCode(code, pending.signal));
      message = applied ? this.dataset.msgApplied : this.dataset.msgRefused;
    } catch (error) {
      // Called off (see disconnectedCallback): the newer render on the page
      // already says what is true, so there is nothing to add to it.
      if (error?.name === 'AbortError') return;
      message = this.dataset.msgFailed;
    } finally {
      if (this.pending === pending) this.pending = null;
    }
    this.setState('idle');
    this.sync();

    if (this.dataset.form) {
      this.toCart(cart);
    } else {
      await this.copy(code);
    }
    // An applied code STAYS applied on screen: the message and the check
    // are the state of the cart, not a notification about it.
    this.announce(message, { stay: applied });
  }

  /*
   * The product page's code button, on to the cart (data-form, above).
   * Whether the cue is in the cart is the answer's word, for the variant
   * Dawn's form would add; with no answer (the cart could not be reached)
   * the add goes ahead. Dawn's handler does the rest, drawer included, and
   * without a drawer on the page (a merchant setting) it goes to the cart
   * page — as this does for a cue already in the cart.
   */
  toCart(cart) {
    const form = document.getElementById(this.dataset.form);
    const variant = form?.querySelector('[name="id"]')?.value;
    const held = Boolean(variant) && (cart?.items || []).some((item) => String(item.variant_id) === variant);

    if (form && !held) {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
      return;
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(this.button);
    } else {
      window.location = routes.cart_url;
    }
  }

  setState(state) {
    this.button.dataset.state = state;
    this.button.setAttribute('aria-busy', String(state === 'busy'));
  }

  async copy(code) {
    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      // Insecure context, or the user denied clipboard access. A selected
      // code the customer can copy by hand beats a button that does nothing.
      this.selectFallback(code);
    }
  }

  selectFallback(code) {
    const field = document.createElement('input');
    field.value = code;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    (this.isConnected ? this : document.body).appendChild(field);
    field.select();
    try {
      document.execCommand('copy');
    } catch (error) {
      /* nothing left to try — the toast still confirms the code is on screen */
    }
    field.remove();
  }

  announce(message, { stay = false } = {}) {
    if (!message) return;
    // The cart's own box sits inside the section its tap re-renders, so by
    // now it may be gone, status line and all. The layout's toast says it
    // instead: it outlives the replaced region (kp-toast.js, CONTEXT
    // glossary "Toast").
    if (!this.isConnected) {
      document.dispatchEvent(new CustomEvent('kp:toast', { detail: { message } }));
      return;
    }
    if (!this.toast) return;
    this.toast.textContent = message;
    this.toast.hidden = false;
    this.toast.classList.toggle('kp-offer__toast--stay', stay);
    clearTimeout(this.timer);
    if (stay) return;
    this.timer = setTimeout(() => {
      this.toast.hidden = true;
    }, 3200);
  }
}

// Every cart change made elsewhere moves the generation, and every answer
// among them that carries the code list is shown: the drawer's code field
// (kp-cart-discount), Dawn's quantity steppers, a refresh through
// Shopify.actions. A code removed there un-applies here; one typed there
// applies here. This file's own publishes are marked and skipped: attachCode
// has counted and shown them already.
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.[OWN]) return;
    generation += 1;
    reflect(event?.cartData);
  });
}

window.kpApplyCode = (code, options) => {
  const wanted = normalise(code);
  const signal = options?.signal;
  if (!wanted || signal?.aborted) return Promise.resolve(false);
  return attachCode(wanted, signal)
    .then((answer) => answer.applied)
    .catch(() => false);
};

customElements.define('kp-copy-code', KpCopyCode);
})();
